// Business time of the Cockpit: every appointment and callback time an agent
// enters or sees is German time, wherever the agent's PC is located. The
// agent's PC timezone must never decide what is sent, stored or shown. Built
// on Intl only. The backend carries the same code in helper/business-time.js;
// keep both and their test vectors in sync.
export const BUSINESS_TIME_ZONE = "Europe/Berlin";

const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const MINUTE_IN_MS = 60 * 1000;
// moment's default (English) short month names, as shown in the panel so far
const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

export const BUSINESS_TIME_ERRORS = {
  INVALID: "INVALID_APPOINTMENT_TIME",
  NONEXISTENT: "NONEXISTENT_LOCAL_TIME"
};

const partsFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: BUSINESS_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
});

const businessTimeError = (message) => {
  const error = new Error(message);
  error.code = 400;
  return error;
};

const pad = (value) => String(value).padStart(2, "0");

const toValidDate = (instant) => {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) {
    throw businessTimeError(BUSINESS_TIME_ERRORS.INVALID);
  }
  return date;
};

// Wall clock fields of an instant in business time.
const businessParts = (instant) => {
  const parts = {};
  partsFormatter.formatToParts(toValidDate(instant)).forEach((part) => {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  });
  return parts;
};

const parseLocal = (localDateTime) => {
  const match = LOCAL_DATE_TIME_PATTERN.exec(
    typeof localDateTime === "string" ? localDateTime : ""
  );
  if (!match) throw businessTimeError(BUSINESS_TIME_ERRORS.INVALID);
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const check = new Date(Date.UTC(year, month - 1, day, hour, minute));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day ||
    hour > 23 ||
    minute > 59
  ) {
    throw businessTimeError(BUSINESS_TIME_ERRORS.INVALID);
  }
  return { year, month, day, hour, minute };
};

const applyPattern = (parts, pattern) =>
  pattern.replace(/YYYY|MMM|MM|DD|HH|mm/g, (token) => {
    switch (token) {
      case "YYYY":
        return String(parts.year);
      case "MMM":
        return SHORT_MONTHS[parts.month - 1];
      case "MM":
        return pad(parts.month);
      case "DD":
        return pad(parts.day);
      case "HH":
        return pad(parts.hour);
      default:
        return pad(parts.minute);
    }
  });

// Offset of business time at an instant, in minutes east of UTC.
export const businessOffsetMinutes = (instant) => {
  const date = toValidDate(instant);
  const parts = businessParts(date);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const wholeSeconds = Math.floor(date.getTime() / 1000) * 1000;
  return Math.round((asUtc - wholeSeconds) / MINUTE_IN_MS);
};

// "YYYY-MM-DDTHH:mm" typed in business time -> Date (UTC instant).
// A time that does not exist (start of summer time) is rejected; a time that
// exists twice (end of summer time) resolves to the first occurrence.
export const localToInstant = (localDateTime) => {
  const { year, month, day, hour, minute } = parseLocal(localDateTime);
  const wallClock = Date.UTC(year, month - 1, day, hour, minute);
  // the offsets valid a day before and after cover both sides of a switch
  const offsets = new Set(
    [wallClock - DAY_IN_MS, wallClock, wallClock + DAY_IN_MS].map(
      businessOffsetMinutes
    )
  );
  const candidates = [...offsets]
    .map((offset) => wallClock - offset * MINUTE_IN_MS)
    .filter((candidate) => {
      const parts = businessParts(candidate);
      return (
        parts.year === year &&
        parts.month === month &&
        parts.day === day &&
        parts.hour === hour &&
        parts.minute === minute
      );
    })
    .sort((a, b) => a - b);
  if (!candidates.length) {
    throw businessTimeError(BUSINESS_TIME_ERRORS.NONEXISTENT);
  }
  return new Date(candidates[0]);
};

// Instant -> text in business time. Tokens: YYYY MM MMM DD HH mm.
export const formatBusiness = (instant, pattern = "DD.MM.YYYY HH:mm") =>
  applyPattern(businessParts(instant), pattern);

// Instant -> "YYYY-MM-DDTHH:mm" in business time (datetime-local inputs).
export const instantToLocalInput = (instant) =>
  formatBusiness(instant, "YYYY-MM-DDTHH:mm");

// Typed wall clock -> text, without any timezone conversion.
export const formatLocal = (localDateTime, pattern = "DD.MM.YYYY HH:mm") =>
  applyPattern(parseLocal(localDateTime), pattern);

// Typed wall clock -> weekday name of that calendar day.
export const weekdayOfLocal = (localDateTime, locale = "de") => {
  const { year, month, day } = parseLocal(localDateTime);
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: "UTC"
  }).format(new Date(Date.UTC(year, month - 1, day)));
};

// The date pickers hand over a Date built in the PC's timezone. What the
// agent sees and types are its local fields; they are taken as they are and
// never converted with the PC's timezone.
export const pickerDateToLocal = (date) => {
  const picked = toValidDate(date);
  return `${picked.getFullYear()}-${pad(picked.getMonth() + 1)}-${pad(
    picked.getDate()
  )}T${pad(picked.getHours())}:${pad(picked.getMinutes())}`;
};

export const getClientTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

// Is the PC currently on the same offset as business time?
export const isClientOnBusinessTime = (now = new Date()) =>
  -now.getTimezoneOffset() === businessOffsetMinutes(now);
